import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../Logo';
import { PageAtmosphere } from '../design-system/PageAtmosphere';
import { MarketingInstrumentDemo } from '../marketing/MarketingInstrumentDemo';
import { createAuthShellViewportLock } from '../../lib/authShellViewportLock.js';
import { usePublicMarketingChrome } from '../marketing/publicMarketingChrome';
import {
  AUTH_FORM_COLUMN_CLASS,
  AUTH_FORM_COLUMN_TESTID,
  AUTH_SHELL_BRAND_TESTID,
  AUTH_SHELL_ROOT_CLASS,
  AUTH_SHELL_STAGE_CLASS,
  AUTH_SHELL_STAGE_MIN_HEIGHT_STYLE,
  AUTH_SHELL_STAGE_TESTID,
  AUTH_SHELL_STAGE_WITH_NAV_CLASS,
} from '../../lib/wwwPhoneClipLayout';

/**
 * Shared auth layout: atmosphere + centered form column.
 * Product demo is opt-in (showDemo) for experiments only - default is form-first.
 */
export function AuthInstrumentShell({
  children,
  subtitle,
  showDemo = false,
  demoVariant = 'hero',
}) {
  const withMarketingNav = usePublicMarketingChrome();
  const stageRef = useRef(null);

  useEffect(() => {
    const tracker = createAuthShellViewportLock({
      getStage: () => stageRef.current,
      visualViewport: window.visualViewport,
      getViewportSize: () => ({
        height: window.innerHeight,
        width: window.innerWidth,
      }),
      requestFrame: (cb) => requestAnimationFrame(cb),
      addWindowListener: (type, fn) => window.addEventListener(type, fn),
      removeWindowListener: (type, fn) => window.removeEventListener(type, fn),
    });
    return () => tracker.dispose();
  }, []);

  return (
    <div className={AUTH_SHELL_ROOT_CLASS}>
      {withMarketingNav ? null : <PageAtmosphere contained />}
      <div
        ref={stageRef}
        data-testid={AUTH_SHELL_STAGE_TESTID}
        className={withMarketingNav ? AUTH_SHELL_STAGE_WITH_NAV_CLASS : AUTH_SHELL_STAGE_CLASS}
        style={AUTH_SHELL_STAGE_MIN_HEIGHT_STYLE}
      >
        {showDemo ? (
          <div className="box-border w-full min-w-0 max-w-6xl grid lg:grid-cols-2 gap-8 lg:gap-12 items-center lg:items-start lg:pt-8 px-4">
            <div className="hidden lg:block lg:sticky lg:top-24">
              <p className="instrument-page-eyebrow mb-2">Performance instrument</p>
              <h2 className="font-display text-2xl font-bold text-[var(--text-primary)] mb-2 leading-tight">
                Know if this ride will empty you - and what to eat.
              </h2>
              <p className="mb-4 text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
                Product preview - illustration only
              </p>
              <MarketingInstrumentDemo variant={demoVariant} preview />
            </div>

            <AuthFormColumn
              subtitle={subtitle}
              hideSubtitleOnLarge
              showBrand={!withMarketingNav}
            >
              {children}
            </AuthFormColumn>
          </div>
        ) : (
          <AuthFormColumn subtitle={subtitle} showBrand={!withMarketingNav}>
            {children}
          </AuthFormColumn>
        )}
      </div>
    </div>
  );
}

function AuthFormColumn({
  children,
  subtitle,
  hideSubtitleOnLarge = false,
  showBrand = true,
}) {
  return (
    <div
      data-testid={AUTH_FORM_COLUMN_TESTID}
      className={[
        AUTH_FORM_COLUMN_CLASS,
        hideSubtitleOnLarge ? 'lg:max-w-md lg:mx-0 lg:ml-auto' : '',
      ].filter(Boolean).join(' ')}
    >
      {showBrand ? (
        <div className="text-center mb-5" data-testid={AUTH_SHELL_BRAND_TESTID}>
          <Link to="/" className="inline-block">
            <Logo size={64} variant="color" showText textClass="text-2xl sm:text-3xl" />
          </Link>
          {subtitle ? (
            <p
              className={[
                'mt-3 text-sm text-[var(--text-secondary)] max-w-sm mx-auto leading-relaxed',
                hideSubtitleOnLarge ? 'lg:hidden' : '',
              ].filter(Boolean).join(' ')}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
